/* ============================================================================
   FF Details/Accordion Animation — site-wide
   Uses Web Animations API for reliable height-based open/close.
   Covers: PDP accordions, collapsible-content, facet dropdowns.

   Clipping is handled entirely by CSS (clip-path on .ff-animating), NOT by
   overflow:hidden. This avoids Block Formatting Context side effects that
   break scroll containers (filter drawer shadows, scroll anchoring, etc.).

   Height is locked before toggling [open] so parent scroll containers
   never see a single-frame content jump.

   Session 45 (Jul 2026) — end-of-open snap fix:
   1. Open target height now adds computed border widths to scrollHeight
      (scrollHeight excludes borders; the close path always compensated,
      which is why only opening snapped).
   2. All measurements use getBoundingClientRect (fractional) instead of
      offsetHeight (integer-rounded).
   3. Animations use fill:'forwards' and are explicitly released in
      cleanup(), so the settle frame can never revert to the pre-animation
      height lock on engines that paint between finish and the event.
   ============================================================================ */
(function () {
  'use strict';

  /* ── Duration ────────────────────────────────────────────────────────────
     Open scales with height so tall panels get visible easing.
     Close is faster — collapsing off-screen content needs to feel immediate.
     ──────────────────────────────────────────────────────────────────────── */
  var OPEN_MIN   = 200;
  var OPEN_MAX   = 600;
  var OPEN_RATE  = 0.35;

  var CLOSE_MIN  = 150;
  var CLOSE_MAX  = 350;
  var CLOSE_RATE = 0.20;

  var EASING = 'ease';

  function calcDuration(deltaH, isClose) {
    var min  = isClose ? CLOSE_MIN  : OPEN_MIN;
    var max  = isClose ? CLOSE_MAX  : OPEN_MAX;
    var rate = isClose ? CLOSE_RATE : OPEN_RATE;
    var d = min + Math.abs(deltaH) * rate;
    return Math.min(Math.max(d, min), max);
  }

  /* ── Selectors ───────────────────────────────────────────────────────── */
  var SEL = [
    '.accordion > details',
    'details.facets__disclosure-vertical',
    'details.mobile-facets__details--dropdown',
    'details.ff-cp__note-details'
  ].join(',');

  var running = new WeakMap();

  /* ── Click handler ───────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var summary = e.target.closest('summary');
    if (!summary) return;

    var details = summary.parentElement;
    if (!details || !details.matches || !details.matches(SEL)) return;

    e.preventDefault();

    /* Cancel any in-progress animation seamlessly:
       Capture the mid-animation rendered height, suppress callbacks so
       cleanup doesn't fire, cancel the animation, then re-lock to the
       captured height. .ff-animating stays on — no unclipped frame. */
    var prev = running.get(details);
    if (prev) {
      var midH = details.getBoundingClientRect().height;
      prev.onfinish = null;
      prev.oncancel = null;
      prev.cancel();
      running.delete(details);
      details.style.height = midH + 'px';
    }

    if (details.open) {
      closeDetails(details);
    } else {
      openDetails(details);
    }
  });

  /* ── Open ─────────────────────────────────────────────────────────────
     Sequence: lock height → add .ff-animating (CSS clips) → set [open]
     → measure via scrollHeight (no height:auto flash) → animate.
     ──────────────────────────────────────────────────────────────────── */
  function openDetails(details) {
    var startH = details.getBoundingClientRect().height;

    /* Lock height, add clipping class */
    details.style.height = startH + 'px';
    details.classList.add('ff-animating');

    /* Open — content renders but is clipped to startH */
    details.open = true;

    /* Measure expanded height via scrollHeight.
       Unlike height:auto, this never changes the rendered box,
       so no scroll anchoring shift occurs.
       scrollHeight = content + padding only — it EXCLUDES borders, while
       the height:auto restored in cleanup() includes them. Add the computed
       border widths back or the settle snaps by the border total. */
    var csOpen = getComputedStyle(details);
    var endH = details.scrollHeight
      + parseFloat(csOpen.borderTopWidth)
      + parseFloat(csOpen.borderBottomWidth);

    /* Force browser to commit startH before animation begins */
    void details.offsetHeight;

    var anim = details.animate(
      { height: [startH + 'px', endH + 'px'] },
      { duration: calcDuration(endH - startH, false), easing: EASING, fill: 'forwards' }
    );

    running.set(details, anim);
    anim.onfinish = function () { cleanup(details); };
    anim.oncancel = function () { cleanup(details); };
  }

  /* ── Close ────────────────────────────────────────────────────────── */
  function closeDetails(details) {
    var startH = details.getBoundingClientRect().height;

    var summaryEl = details.querySelector('summary');
    var cs = getComputedStyle(details);
    var borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    var padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    var endH = summaryEl.getBoundingClientRect().height + borders + padding;

    details.style.height = startH + 'px';
    details.classList.add('ff-animating');

    var anim = details.animate(
      { height: [startH + 'px', endH + 'px'] },
      { duration: calcDuration(startH - endH, true), easing: EASING, fill: 'forwards' }
    );

    running.set(details, anim);
    anim.onfinish = function () {
      details.open = false;
      cleanup(details);
    };
    anim.oncancel = function () { cleanup(details); };
  }

  /* ── Cleanup ──────────────────────────────────────────────────────── */
  function cleanup(details) {
    /* Release the fill:'forwards' hold first — callbacks are nulled so
       cancel() can't re-enter cleanup. Everything below runs in one task,
       so the browser paints only the final settled state. */
    var anim = running.get(details);
    if (anim) {
      anim.onfinish = null;
      anim.oncancel = null;
      anim.cancel();
    }
    running.delete(details);

    details.classList.remove('ff-animating');
    details.style.height = '';

    /* Notify scroll containers that layout has settled */
    details.dispatchEvent(new CustomEvent('ff:accordion-settled', {
      bubbles: true
    }));
  }
})();